import {
  Col,
  AlignItems,
  Box,
  Display,
  FlexDirection,
  Heading1,
  JustifyContent,
  Grid,
} from '@gousto-internal/citrus-react'
import { datadogRum } from '@datadog/browser-rum'
import { browserHistory } from 'react-router'

import { useDashboardExperience } from './hooks/useDashboardExperience'
import { DashboardActions } from './components/DashboardActions'
import { Notification } from './components/Notification'
import React, { useEffect, useState } from 'react'
import { OrderTabs, OrderTile } from './components/OrderTile/index'
import { OrderRecommendations } from './components/OrderRecommendations/OrderRecommendations'
import { useDashboardActionTracking } from './hooks/useDashboardActionTracking'
import { useOrderTracking } from './hooks/useOrderTracking'

const dashboardActionRoutes: Record<string, string> = {
  futureDeliveries: '/my-deliveries',
  getHelp: '/get-help',
  manageSubscription: '/subscription-settings',
  orderBox: '/delivery-details',
  raf: '/my-referrals',
}

const getDefaultActiveTabIndex = (
  orderTabs: ReturnType<typeof useDashboardExperience>['order_tabs'],
) => {
  const defaultActiveIndex = orderTabs.findIndex((tab) => tab.default_active)

  return defaultActiveIndex === -1 ? 0 : defaultActiveIndex
}

const getActiveTabIndex = (
  orderTabs: ReturnType<typeof useDashboardExperience>['order_tabs'],
  activeOrderId: string | undefined,
) => {
  const activeTabIndexFromOrderId = orderTabs.findIndex((tab) => tab.order_id === activeOrderId)

  return activeTabIndexFromOrderId === -1
    ? getDefaultActiveTabIndex(orderTabs)
    : activeTabIndexFromOrderId
}

export function MyHomePage() {
  const {
    page_title,
    order_tabs,
    error,
    orders = [],
    actions = [],
    downloadApp,
    rafCampaign,
    rafStatus,
  } = useDashboardExperience()
  const { trackDashboardActionClick } = useDashboardActionTracking()
  const { trackHomeOrdersTabClick } = useOrderTracking()
  const [activeOrderId, setActiveOrderId] = useState<string>()
  const [pendingSelectedDeliveryDate, setPendingSelectedDeliveryDate] = useState<string>()
  const [displayedOrderTabs, setDisplayedOrderTabs] = useState(order_tabs)
  const [displayedOrders, setDisplayedOrders] = useState(orders)
  const hasOrderMismatch = displayedOrders.length !== displayedOrderTabs.length
  const hasDashboardActions = actions.some((action) => {
    if (action.type === 'downloadApp') {
      return Boolean(downloadApp)
    }

    if (action.type === 'raf') {
      return Boolean(rafStatus || rafCampaign)
    }

    return true
  })

  const activeTabIndex = getActiveTabIndex(displayedOrderTabs, activeOrderId)

  const activeOrder = displayedOrders.find((order) => order.id === activeOrderId)

  useEffect(() => {
    setDisplayedOrderTabs(order_tabs)
  }, [order_tabs])

  useEffect(() => {
    setDisplayedOrders(orders)
  }, [orders])

  useEffect(() => {
    if (!displayedOrderTabs.length) {
      return
    }

    if (pendingSelectedDeliveryDate) {
      const matchingOrder = displayedOrders.find(
        (order) => order.analytics.delivery_date === pendingSelectedDeliveryDate,
      )

      if (matchingOrder) {
        setActiveOrderId(matchingOrder.id)
        setPendingSelectedDeliveryDate(undefined)
        return
      }
    }

    if (activeOrderId && displayedOrderTabs.some((tab) => tab.order_id === activeOrderId)) {
      return
    }

    const defaultActiveTab = displayedOrderTabs[getDefaultActiveTabIndex(displayedOrderTabs)]
    setActiveOrderId(defaultActiveTab.order_id)
  }, [displayedOrderTabs, displayedOrders, activeOrderId, pendingSelectedDeliveryDate])

  const handleOrderTabChange = (index: number) => {
    const selectedTab = displayedOrderTabs[index]

    setActiveOrderId(selectedTab.order_id)

    const order = displayedOrders.find((currentOrder) => currentOrder.id === selectedTab.order_id)

    if (order) {
      trackHomeOrdersTabClick({
        order,
        tabPosition: index + 1,
      })
    }
  }

  const handleDashboardActionClick = (actionType: string) => {
    trackDashboardActionClick(actionType)

    const route = dashboardActionRoutes[actionType]

    if (route) {
      browserHistory.push(route)
    }
  }

  const handleOrderSkipSuccess = (orderId: string) => {
    setDisplayedOrders((currentOrders) =>
      currentOrders.map((currentOrder) =>
        currentOrder.id === orderId
          ? {
              ...currentOrder,
              help_cta: null,
              restore_cta: currentOrder.restore_cta ?? 'Restore order',
              state: 'skip',
              state_title: 'Skipped',
              price: {
                ...currentOrder.price,
                amount: null,
                original: null,
                kind: null,
              },
            }
          : currentOrder,
      ),
    )
    setDisplayedOrderTabs((currentTabs) =>
      currentTabs.map((currentTab) =>
        currentTab.order_id === orderId
          ? {
              ...currentTab,
              discount: null,
              icon: 'cancelled',
            }
          : currentTab,
      ),
    )
  }

  useEffect(() => {
    if (hasOrderMismatch) {
      datadogRum.addAction('dashboard_experience_order_mismatch', {
        page: 'MyHomePage',
        selectedIndex: activeTabIndex,
        orderTabsLength: displayedOrderTabs.length,
        ordersLength: displayedOrders.length,
      })
    }
  }, [hasOrderMismatch, activeTabIndex, displayedOrderTabs.length, displayedOrders.length])

  return (
    <main data-testid="my-home-page-main">
      <Box
        maxWidth="1200px"
        style={{ margin: '0 auto' }}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        alignItems={AlignItems.FlexStart}
        gap={[4, 6]}
        paddingV={[4, 6]}
        paddingH={[3, 0]}
      >
        <Box
          width="100%"
          minHeight={['52px', '40px']}
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.FlexStart}
          paddingV={[3, 1]}
          paddingH={[3, 0]}
          data-testid="my-home-page-title-box"
        >
          {error ? (
            <Notification
              state="error"
              message="Oops something went wrong. Please try again later."
            />
          ) : (
            <Heading1>{page_title}</Heading1>
          )}
        </Box>

        <Grid gap={[4, 3]} width={'100%'}>
          <Col size={[12, null, null, 2]} display={[Display.None, null, null, Display.Block]}>
            <Box>
              <section aria-label="order-tabs">
                <Box>
                  <OrderTabs
                    orderTabs={displayedOrderTabs}
                    activeIndex={activeTabIndex}
                    onChange={handleOrderTabChange}
                  />
                </Box>
              </section>
            </Box>
          </Col>
          <Col size={[12, null, null, 10]}>
            <Box
              width="100%"
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              gap={[4, 3]}
            >
              <section aria-label="Right column">
                {!hasOrderMismatch && activeOrder ? (
                  <Box width="100%" display={[Display.Flex, null, null, Display.None]}>
                    <section
                      aria-label="order-tabs-mobile"
                      style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                    >
                      <OrderTabs
                        orderTabs={displayedOrderTabs}
                        activeIndex={activeTabIndex}
                        onChange={handleOrderTabChange}
                        variant="mobile"
                      />
                    </section>
                  </Box>
                ) : null}
                {hasOrderMismatch ? (
                  <Notification
                    state="error"
                    message="Oops something went wrong. Please try again later."
                  />
                ) : activeOrder ? (
                  <Box
                    width="100%"
                    display={Display.Flex}
                    flexDirection={FlexDirection.Column}
                    gap={[4, 4]}
                  >
                    <OrderTile
                      onDeliveryDateSaved={(selectedDeliveryDate) =>
                        setPendingSelectedDeliveryDate(selectedDeliveryDate)
                      }
                      onSkipSuccess={handleOrderSkipSuccess}
                      order={activeOrder}
                    />
                    <OrderRecommendations
                      newAndLimited={activeOrder.new_and_limited}
                      orderId={activeOrder.id}
                      topPicks={activeOrder.top_picks}
                    />
                  </Box>
                ) : null}
              </section>

              {hasDashboardActions ? (
                <section aria-label="dashboard actions" style={{ width: '100%' }}>
                  <DashboardActions
                    actions={actions}
                    downloadApp={downloadApp}
                    rafCampaign={rafCampaign}
                    rafStatus={rafStatus}
                    onActionClick={handleDashboardActionClick}
                  />
                </section>
              ) : null}
            </Box>
          </Col>
        </Grid>
      </Box>
    </main>
  )
}
